"use client";

import Image from "next/image";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

import { withCloudinaryAutoFormat } from "@/lib/cloudinaryImageUrl";

export interface BodyLightboxImage {
  key: string;
  url: string;
  alt: string;
  caption?: string;
}

interface BodyImageLightboxContextValue {
  images: BodyLightboxImage[];
  metaByKey: Record<string, { alt: string; caption?: string }>;
  openByKey: (key: string) => void;
}

const BodyImageLightboxContext = createContext<BodyImageLightboxContextValue>({
  images: [],
  metaByKey: {},
  openByKey: () => {},
});

export const useBodyImageLightbox = () => useContext(BodyImageLightboxContext);

interface ProviderProps {
  images: BodyLightboxImage[];
  children: ReactNode;
}

export const BodyImageLightboxProvider = ({
  images,
  children,
}: ProviderProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);

  const metaByKey: Record<string, { alt: string; caption?: string }> = {};
  images.forEach((image) => {
    metaByKey[image.key] = { alt: image.alt, caption: image.caption };
  });

  const isOpen = activeIndex !== null;

  const openByKey = (key: string) => {
    const index = images.findIndex((image) => image.key === key);
    if (index >= 0) setActiveIndex(index);
  };

  const handleClose = () => setActiveIndex(null);

  const handlePrev = () => {
    setActiveIndex((prev) =>
      prev === null ? prev : (prev - 1 + images.length) % images.length
    );
  };

  const handleNext = () => {
    setActiveIndex((prev) =>
      prev === null ? prev : (prev + 1) % images.length
    );
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
      if (event.key === "ArrowLeft") handlePrev();
      if (event.key === "ArrowRight") handleNext();
    };

    document.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, images.length]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const endX = event.changedTouches[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    const SWIPE_THRESHOLD = 50;
    if (delta > SWIPE_THRESHOLD) handlePrev();
    else if (delta < -SWIPE_THRESHOLD) handleNext();
    touchStartX.current = null;
  };

  const activeImage = activeIndex !== null ? images[activeIndex] : null;

  return (
    <BodyImageLightboxContext.Provider
      value={{ images, metaByKey, openByKey }}
    >
      {children}

      {isOpen && activeImage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 sm:p-8"
          onClick={handleClose}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close fullscreen"
            className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <X className="size-6" />
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handlePrev();
                }}
                aria-label="Previous image"
                className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:left-4"
              >
                <ChevronLeft className="size-7" />
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleNext();
                }}
                aria-label="Next image"
                className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-4"
              >
                <ChevronRight className="size-7" />
              </button>
            </>
          )}

          <figure
            className="relative flex max-h-full w-full max-w-5xl flex-col items-center"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="relative h-[80vh] w-full">
              <Image
                src={activeImage.url}
                alt={activeImage.alt}
                fill
                sizes="100vw"
                className="object-contain"
                priority
              />
            </div>
            {activeImage.caption && (
              <figcaption className="mt-4 text-center text-sm text-white/80">
                {activeImage.caption}
              </figcaption>
            )}
            {images.length > 1 && (
              <span className="mt-2 text-xs text-white/60">
                {(activeIndex ?? 0) + 1} / {images.length}
              </span>
            )}
          </figure>
        </div>
      )}
    </BodyImageLightboxContext.Provider>
  );
};

interface CloudinaryBodyImageProps {
  imageKey?: string;
  secureUrl: string;
  fallbackAlt?: string;
  fallbackCaption?: string;
  linkUrl?: string;
  openInNewTab?: boolean;
}

export const CloudinaryBodyImage = ({
  imageKey,
  secureUrl,
  fallbackAlt,
  fallbackCaption,
  linkUrl,
  openInNewTab,
}: CloudinaryBodyImageProps) => {
  const { metaByKey, openByKey } = useBodyImageLightbox();
  const meta = imageKey ? metaByKey[imageKey] : undefined;

  const altText = meta?.alt || fallbackAlt || "Post image";
  const caption = meta?.caption || fallbackCaption || "";
  const imageUrl = withCloudinaryAutoFormat(secureUrl);

  const imageElement = (
    <Image
      src={imageUrl}
      alt={altText}
      height={700}
      width={700}
      className="mx-auto rounded-2xl"
      loading="lazy"
    />
  );

  // External link images keep their link behavior.
  if (linkUrl) {
    return (
      <figure className="my-4">
        <a
          href={linkUrl}
          target={openInNewTab ? "_blank" : undefined}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
        >
          {imageElement}
        </a>
        {caption && (
          <figcaption className="mt-2 text-center italic text-hims-medium-gray">
            {caption}
          </figcaption>
        )}
      </figure>
    );
  }

  return (
    <figure className="my-4">
      <button
        type="button"
        onClick={() => imageKey && openByKey(imageKey)}
        aria-label={`View ${altText} in fullscreen`}
        className="block w-full cursor-zoom-in border-0 bg-transparent p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
      >
        {imageElement}
      </button>
      {caption && (
        <figcaption className="mt-2 text-center italic text-hims-medium-gray">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};
